import React, { type JSX } from "react"
import "./organization-settings.css"
import { InlineTextInput } from "@nova/component-inline-text-input"
import { CheckboxInput } from "@nova/component-checkbox-input"
import { RadioInput } from "@nova/component-radio-input"
import { useTranslation } from "@nova/use-translation"
import { HelpTooltip } from "@nova/component-help-tooltip"
import { Select } from "@nova/component-select"
import { OrganizationSettings as OrganizationSettingsType } from "@nova/schemas"

export type formItem = {
    name: string
    title: string
    type: "Radio" | "Text" | "Checkbox" | "Arbitrary" | "Dropdown"
    defaultValue?: string | boolean
    options?: { label: string; value: string }[] //used for radio buttons
    label?: string //used for checboxes
    tooltip?: { content: string; href: string }
    Component?: ({
        name,
        value,
        onChange,
    }: {
        name: string
        value: string
        onChange: (name: string, value: string) => void
    }) => JSX.Element
}

type OrganizationSettingsProps = {
    formItems: formItem[]
    editorValues: OrganizationSettingsType
    onChange: (name: string, value: string | boolean | number) => void
    disabled?: Record<string, boolean>
    disableAll?: boolean
}

export const OrganizationSettings = ({
    formItems,
    editorValues,
    onChange,
    disabled,
    disableAll,
}: OrganizationSettingsProps) => {
    return (
        <div className="organization-settings">
            {formItems.map((item) => {
                const values =
                    editorValues?.find((e) => e.name === item.name)?.value || item.defaultValue
                const isDisabled = disabled?.[item.name]
                return (
                    <InputGroup
                        key={item.name}
                        formItem={item}
                        value={values}
                        onChange={onChange}
                        disabled={disableAll || isDisabled}
                    />
                )
            })}
        </div>
    )
}

type InputGroupProps = {
    formItem: formItem
    value?: string | boolean | number
    onChange: (name: string, value: string | boolean | number) => void
    disabled?: boolean
}

const InputGroup = ({ formItem, value, onChange, disabled }: InputGroupProps) => {
    const { name, title, type, options, label, tooltip, Component } = formItem
    const { t } = useTranslation()
    let ControlComponent = Text
    if (type === "Radio") {
        ControlComponent = Radio
    }
    if (type === "Checkbox") {
        ControlComponent = Checkbox
    }
    if (type === "Dropdown") {
        ControlComponent = Dropdown
    }
    if (type === "Arbitrary" && Component) {
        ControlComponent = Component as () => JSX.Element
    }
    return (
        <div>
            <div className="flex items-center gap-2">
                <h5 className="organization-settings-title nova-label">{t(title)}</h5>
                {tooltip && (
                    <HelpTooltip
                        content={tooltip?.content}
                        href={tooltip?.href}
                        theme="light"
                        size={12}
                    />
                )}
            </div>
            <ControlComponent
                name={name}
                value={value}
                onChange={onChange}
                options={options}
                label={label}
                disabled={disabled}
            />
        </div>
    )
}

type InputFieldProps = {
    name: string
    value?: string | boolean | number
    label?: string
    options?: { label: string; value: string }[]
    onChange: (name: string, value: string | boolean) => void
    disabled?: boolean
}

const Radio = ({ name, options, value, onChange, disabled }: InputFieldProps) => {
    if (value !== undefined && typeof value !== "string") {
        throw "invalid value passed to radio field"
    }
    if (!options) {
        throw "options must be provided to a radio input"
    }

    return (
        <RadioInput
            value={value}
            options={options}
            onChange={(selected) => onChange(name, selected)}
            disabled={disabled}
        />
    )
}

const Text = ({ name, value, onChange, disabled }: InputFieldProps) => {
    if (typeof value !== "string") {
        throw "invalid value passed to text field"
    }
    return (
        <InlineTextInput
            name={name}
            handleChange={(value) => onChange(name, value)}
            value={value}
            data-testid="organization-settings-input"
            disabled={disabled}
        />
    )
}

const Checkbox = ({ name, label, value, onChange, disabled }: InputFieldProps) => {
    if (typeof value !== "boolean") {
        throw "invalid value passed to checkbox field"
    }

    return (
        <CheckboxInput
            id={name}
            onChange={(newValue) => onChange(name, newValue)}
            value={value}
            label={label}
            disabled={disabled}
        />
    )
}

const Dropdown = ({ name, value, onChange, options = [], disabled }: InputFieldProps) => {
    if (!options || typeof value !== "string") {
        throw " invalid value passed to dropdown field"
    }
    return (
        <Select<string>
            options={options}
            onChange={(v) => v && onChange(name, v)}
            value={value}
            disabled={disabled}
        />
    )
}
